import { create } from "zustand";
import { Event, Theme } from "./types";

//TODO:
// Remove unknown
// Move to another location
export interface ResultsData {
  bestSegmentDuration: number;
  bestSegmentDurationReadable: unknown;
  bib: string;
  category: string;
  startTime: number;
  startTimeString: string;
  finishTime: number;
  finishTimeString: string;
  duration: number;
  durationHumanReadible: string;
  eliteFlag: boolean;
  eventId: string;
  gender: string;
  heat: string;
  heatId: string;
  id: string;
  lastSegmentFinishTimeReadable: unknown;
  name: string;
  nationalityId: unknown;
  numberOfRunSegments: unknown;
  participantId: string;
  pointNameToTime: unknown;
  rankingPosition: unknown;
  resultForMostLaps: boolean;
  runTimeBySegmentsReadable: unknown;
  teamName: string;
  ticketType: string;
  transitionDuration: boolean;
  transitionDurationReadable: unknown;
  transitionTimeBySegments: unknown;
  transitionTimeBySegmentsReadable: unknown;
  userId: string;
}

interface ResultSetResponse {
  id: string;
  eventId: string;
  alias: null;
  name: string;
  themeId: null;
  tables: [
    {
      alias: null;
      name: null;
      rows: {
        category: string[];
        heat: string[];
      };
      columns: { [key: string]: string };
      sort: null;
    }
  ];
}

export interface ResultSet extends ResultSetResponse {
  isActive: boolean;
  groupName: string;
}

export interface ResultGroup {
  name: string;
  isActive: boolean;
}

export interface Store {
  events: Event[];
  themes: Theme[];
  resultsData: ResultsData[];
  resultGroups: ResultGroup[];
  eventId: string;
  isResultsDataLoading: boolean;
  resultSets: ResultSet[];
  setEventId: (eventId: string) => void;
  setResults: (resultsData: ResultsData[]) => void;
  setResultSetActive: (eventId: string, resultSetId: string) => void;
  liveDataRefresh: (eventId: string, activeResultSetId: string) => void;
  setResultGroupActive: (groupName: string, resultSets: ResultSet[]) => void;
  fetchInitData: (eventId: string) => void;
  //TODO:
  themeSettings: any;
}

const assignGroupsToResultSet = (resultSet: ResultSetResponse) => {
  const [name, groupName] = resultSet.name.split(";");

  return {
    ...resultSet,
    name,
    groupName,
  };
};

const extractGroupsFromResultSets = (resultSets: ResultSet[]) => {
  const groups = [] as ResultGroup[];

  resultSets.forEach((set) => {
    if (
      set.groupName &&
      !groups.find((group) => group.name === set.groupName)
    ) {
      groups.push({ name: set.groupName, isActive: false });
    }
  });

  return groups;
};

const fetchResultsDataFromApi = async (
  eventId: string,
  resultSetId?: string
) => {
  const resultSetIdUrl = resultSetId ? `?resultsSetId=${resultSetId}` : "";

  return await fetch(
    `${process.env.REACT_APP_API_BASE_URL}/public/rest/result/eventId/${eventId}/stream.json${resultSetIdUrl}`
  ).then(async (f) => await f.json());
};

const useStore = create<Store>((set) => ({
  events: [],
  themes: [],
  resultsData: [],
  resultSets: [],
  resultGroups: [],
  eventId: "",
  themeSettings: {},
  error: true,
  isResultsDataLoading: false,
  setEventId: (eventId: string) => set((state) => ({ ...state, eventId })),
  setResults: (resultsData: ResultsData[]) => {
    set((state) => ({ ...state, resultsData }));
  },
  fetchInitData: async (eventId: string) => {
    set((state) => ({ ...state, isResultsDataLoading: true }));

    const themeSettings = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/public/events/${eventId}/themes/results`
    ).then((data) => data.json());
    const fetchedResultSets = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/public/themes/${themeSettings.id}/results/sets`
    ).then(async (f) => await f.json());

    const resultSets = fetchedResultSets
      .filter((set: ResultSet) => set.name.includes(";"))
      .map((set: ResultSet, index: number) => ({
        ...set,
        isActive: index === 0,
      }))
      .map(assignGroupsToResultSet);

    const activeResultSet = resultSets.find((set: ResultSet) => set.isActive);

    const resultsData = activeResultSet
      ? await fetchResultsDataFromApi(eventId, activeResultSet.id)
      : [];

    const resultGroups = extractGroupsFromResultSets(resultSets);

    set((state) => ({
      ...state,
      isResultsDataLoading: false,
      eventId,
      themeSettings,
      resultSets,
      resultGroups,
      resultsData: resultsData || [],
    }));
  },

  setResultSetActive: async (eventId: string, resultSetId: string) => {
    set((state) => ({ ...state, isResultsDataLoading: true }));

    const fetchedResultsData = await fetchResultsDataFromApi(
      eventId,
      resultSetId
    );
    return set((state) => ({
      ...state,
      resultsData: fetchedResultsData,
      resultSets: state.resultSets.map((set) =>
        set.id === resultSetId
          ? { ...set, isActive: true }
          : { ...set, isActive: false }
      ),
      isResultsDataLoading: false,
    }));
  },

  //Set first result set active within a group
  setResultGroupActive: (groupName: string, resultSets: ResultSet[]) => {
    const resultSetsWithinGroup = resultSets.filter(
      (set) => set.groupName === groupName
    );

    set((state) => ({
      ...state,
      resultSets: resultSets.map((set) =>
        resultSetsWithinGroup.length && set.id === resultSetsWithinGroup[0]?.id
          ? { ...set, isActive: true }
          : { ...set, isActive: false }
      ),
    }));
  },

  liveDataRefresh: async (eventId: string, activeResultSetId: string) => {
    const fetchedResultsData = await fetchResultsDataFromApi(
      eventId,
      activeResultSetId
    );

    return set((state) => ({
      ...state,
      resultsData: fetchedResultsData,
    }));
  },
}));

export default useStore;
